import { t, useLanguage } from '../../i18n';
import React, { useEffect, useMemo, useState } from 'react';
import { FlatList, ScrollView, StyleSheet, View, Text, TouchableOpacity, useWindowDimensions } from 'react-native';
import { ActivityIndicator, Button, Card, Chip, Divider, FAB, useTheme } from 'react-native-paper';
import { Ionicons } from '@expo/vector-icons';
import { auth } from '../../firebase';
import { Course } from '../../types';
import { useCourseReviews } from '../../hooks/useCourseReviews';
import { useCourseSurveyResponses } from '../../hooks/useCourseSurveyResponses';
import { useCourseEnrollmentCount } from '../../hooks/useCourseEnrollmentCount';
import { useLastYearEnrollment } from '../../hooks/useLastYearEnrollment';
import { useReviewTags } from '../../hooks/useReviewTags';
import { useSchedule } from '../../contexts/ScheduleContext';
import { GRADE_KEYS, ReviewerFaculty, ReviewerYearKey } from '../../types/review';
import { isInReviewPeriod, isInGradePeriod } from '../../constants/reviewPeriods';

const productionEnrollmentCounts2026 = require('../../data/enrollmentCounts2026.json') as Record<string, number>;
const developmentEnrollmentCounts2026 = __DEV__
  ? require('../../data/developmentEnrollmentCounts2026.json') as Record<string, number>
  : {};
const enrollmentCounts2026 = __DEV__
  ? { ...productionEnrollmentCounts2026, ...developmentEnrollmentCounts2026 }
  : productionEnrollmentCounts2026;

type Props = {
  navigation: any;
  route: {
    params: {
      course: Course;
    };
  };
};

const MIN_PUBLIC_REVIEW_COUNT = 3;

const SURVEY_RESPONSE_DISPLAY_HEADINGS = {
  advice: '注意・アドバイス',
  positivePoints: '良い・おすすめな点',
  negativePoints: '良くない点',
} as const;

// 成績グループの色定義
const gradeColorMap: Record<string, string> = {
  'S':  '#A8D5A2',
  'A+': '#B5DAB0',
  'A':  '#C0DFBB',
  'A-': '#CAE3C6',
  'B+': '#AACAE8',
  'B':  '#B5D2EC',
  'B-': '#C0DAF0',
  'C+': '#F5C49A',
  'C':  '#F7CCA3',
  'C-': '#F9D4AC',
  'F':  '#F5AAAA',
};

// レビュー統計のスライダーUI
const renderStatSlider = (label: string, minLabel: string, maxLabel: string, value: number, color: string, theme: any) => {
  const percentage = ((value - 1) / 4) * 100;
  const displayValue = value.toFixed(1);

  // 3.0を基準に、寄っている方のラベルを強調
  const isLeft = value < 3.0;
  const isRight = value > 3.0;

  return (
    <View style={{ marginBottom: 24, paddingHorizontal: 8 }}>
      <Text style={{ fontSize: 14, fontWeight: '700', color: theme.colors.onSurface, marginBottom: 32 }}>{t(label)}</Text>
      
      <View style={{ position: 'relative', height: 4, backgroundColor: '#E0E0E0', borderRadius: 2 }}>
        {/* 5点分のスケールティックマーク */}
        {[0, 25, 50, 75, 100].map((pos, i) => (
          <View
            key={i}
            style={{
              position: 'absolute',
              left: `${pos}%`,
              top: -4,
              width: 1,
              height: 12,
              backgroundColor: '#BDBDBD',
              marginLeft: -0.5,
              zIndex: 1,
            }}
          />
        ))}
        {/* 吹き出しとツマミのコンテナ */}
        <View style={{
          position: 'absolute',
          left: `${percentage}%`,
          bottom: -5, // ツマミの中心がトラックの中央に来るように調整
          alignItems: 'center',
          width: 40,
          marginLeft: -20, // コンテナ幅の半分を引いてX軸を中央に
          zIndex: 2,
        }}>
          {/* 吹き出し */}
          <View style={{
            backgroundColor: '#757575',
            borderRadius: 12,
            paddingHorizontal: 8,
            paddingVertical: 2,
            alignItems: 'center',
          }}>
            <Text style={{ color: '#FFF', fontSize: 11, fontWeight: 'bold' }}>{displayValue}</Text>
          </View>
          {/* 下向き三角形 */}
          <View style={{
            width: 0,
            height: 0,
            borderLeftWidth: 4,
            borderRightWidth: 4,
            borderTopWidth: 5,
            borderStyle: 'solid',
            backgroundColor: 'transparent',
            borderLeftColor: 'transparent',
            borderRightColor: 'transparent',
            borderTopColor: '#757575',
            marginBottom: 2,
          }} />
          {/* ツマミ (丸) */}
          <View style={{
            width: 14,
            height: 14,
            borderRadius: 7,
            backgroundColor: color,
          }} />
        </View>
      </View>

      <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginTop: 12 }}>
        {/* 左ラベル */}
        <View style={{
           backgroundColor: isLeft ? color : 'transparent',
           paddingHorizontal: 10,
           paddingVertical: 4,
           borderRadius: 12,
        }}>
           <Text style={{ fontSize: 12, fontWeight: isLeft ? 'bold' : 'normal', color: isLeft ? '#FFF' : theme.colors.onSurfaceVariant }}>{t(minLabel)}</Text>
        </View>
        
        {/* 右ラベル */}
        <View style={{
           backgroundColor: isRight ? color : 'transparent',
           paddingHorizontal: 10,
           paddingVertical: 4,
           borderRadius: 12,
        }}>
           <Text style={{ fontSize: 12, fontWeight: isRight ? 'bold' : 'normal', color: isRight ? '#FFF' : theme.colors.onSurfaceVariant }}>{t(maxLabel)}</Text>
        </View>
      </View>
    </View>
  );
};

// 棒グラフ（成績分布）
const renderBarChart = (gradeDistribution: Record<string, number>, theme: any) => {
  const data = GRADE_KEYS.map((key) => ({ key, value: gradeDistribution[key] || 0 }));
  const total = data.reduce((sum, { value }) => sum + value, 0);

  if (total === 0) {
    return <Text style={[styles.emptyText, { color: theme.colors.onSurfaceVariant }]}>{t("データがありません")}</Text>;
  }

  const maxValue = Math.max(...data.map(({ value }) => value), 1);

  return (
    <View style={styles.gradeBarContainer}>
      {data.map(({ key, value }) => {
        const barWidthPercent = (value / maxValue) * 100;
        const color = gradeColorMap[key] || '#999999';
        return (
          <View key={key} style={styles.gradeBarRow}>
            <Text style={[styles.gradeLabel, { color: theme.colors.onSurface }]}>{key}</Text>
            <View style={[styles.gradeBarTrack, { backgroundColor: theme.colors.surfaceVariant }]}>
              {barWidthPercent > 0 && (
                <View
                  style={[
                    styles.gradeBarFill,
                    { width: `${barWidthPercent}%`, backgroundColor: color },
                  ]}
                />
              )}
            </View>
            <Text style={[styles.gradeCount, { color: theme.colors.onSurface }]}>{value}</Text>
          </View>
        );
      })}
    </View>
  );
};

const YEAR_SEGMENT_CONFIG: Array<{ key: ReviewerYearKey; label: string; color: string }> = [
  { key: '1', label: '1年', color: '#339AF0' },
  { key: '2', label: '2年', color: '#40C057' },
  { key: '3', label: '3年', color: '#FAB005' },
  { key: '4', label: '4年', color: '#E8590C' },
  { key: '5', label: '院1年', color: '#D6336C' },
  { key: '6', label: '院2年', color: '#862E9C' },
];

const FACULTY_SEGMENT_CONFIG: Array<{ key: ReviewerFaculty; label: string; color: string }> = [
  { key: 'lc', label: '言語文化', color: '#339AF0' },
  { key: 'ia', label: '国際社会', color: '#FAB005' },
  { key: 'js', label: '国際日本', color: '#40C057' },
  { key: 'pg', label: '大学院', color: '#862E9C' },
];

type SegmentData = { label: string; value: number; color: string };

const getDayPeriodLabel = (course: Course) => {
  const isOutOfGrid =
    !course.period ||
    course.period < 1 ||
    course.period > 6 ||
    course.dayOfWeek === undefined ||
    course.dayOfWeek < 1 ||
    course.dayOfWeek > 5;

  const hasOnDemandTag =
    (course.name || '').includes('オンデマンド') ||
    (course.name || '').includes('ｵﾝﾃﾞﾏﾝﾄﾞ') ||
    (course.room || '').includes('オンデマンド') ||
    (course.room || '').includes('ｵﾝﾃﾞﾏﾝﾄﾞ') ||
    (course.notes || '').includes('オンデマンド') ||
    (course.notes || '').includes('ｵﾝﾃﾞﾏﾝﾄﾞ');

  if (isOutOfGrid || hasOnDemandTag) return 'オンデマンド';
  if (course.dayOfWeek !== undefined && course.period !== undefined) {
    return `${['', '月', '火', '水', '木', '金', '土', '日'][course.dayOfWeek]}曜${course.period}限`;
  }
  return null;
};

const StackedBar = ({ data, title, theme }: { data: SegmentData[]; title: string; theme: any }) => {
  useLanguage();
  const total = data.reduce((sum, d) => sum + d.value, 0);
  if (total === 0) return null;
  return (
    <View style={{ marginBottom: 12 }}>
      <Text style={{ fontSize: 12, color: theme.colors.onSurfaceVariant, marginBottom: 4 }}>{title}</Text>
      <View style={{ flexDirection: 'row', height: 8, borderRadius: 4, overflow: 'hidden' }}>
        {data.map((seg, i) => (
          <View key={i} style={{ flex: seg.value, backgroundColor: seg.color }} />
        ))}
      </View>
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginTop: 6 }}>
        {data.map((seg, i) => (
          <View key={i} style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
            <View style={{ width: 8, height: 8, borderRadius: 2, backgroundColor: seg.color }} />
            <Text style={{ fontSize: 11, color: theme.colors.onSurfaceVariant }}>
              {t(seg.label)} ({Math.round((seg.value / total) * 100)}%)
            </Text>
          </View>
        ))}
      </View>
    </View>
  );
};

const CourseEvaluationScreen = ({ navigation, route }: Props) => {
  useLanguage();
  const theme = useTheme();
  const { width: windowWidth } = useWindowDimensions();
  const { course } = route.params;

  const parseCourseYear = (year: string | number | undefined): number | null => {
    const normalized = String(year || '').replace(/[^0-9]/g, '');
    const value = parseInt(normalized, 10);
    return Number.isNaN(value) ? null : value;
  };
  const courseYear = parseCourseYear(course.academicYear);
  const availableYears = courseYear ? [String(courseYear - 1), String(courseYear)] : [];
  const defaultYear = availableYears.length > 0 ? availableYears[0] : undefined;
  const [selectedYear, setSelectedYear] = useState<string | undefined>(defaultYear);
  const selectedYearForHook = selectedYear;
  const { reviewCount, stats, gradeDistribution, dataAcademicYear, loading, error } = useCourseReviews(course.id, selectedYearForHook, true);
  const { responses: surveyResponses, loading: surveyLoading, error: surveyError } = useCourseSurveyResponses(course.id, selectedYearForHook);
  const [activeSurveyIndex, setActiveSurveyIndex] = useState(0);
  const { count: dynamicCurrentEnrollmentCount } = useCourseEnrollmentCount(course.id);
  const currentEnrollmentCount = dynamicCurrentEnrollmentCount;

  // 年度別の履修者数スナップショット。学期終了後に生成した固定JSONを使う。
  const enrollmentCountSnapshots: Record<string, Record<string, number>> = {
    '2026': enrollmentCounts2026,
  };
  const getStaticEnrollmentCount = (year: string | number): number | null => {
    const snapshot = enrollmentCountSnapshots[String(year)];
    if (!snapshot) return null;
    const value = snapshot[String(course.id)];
    return typeof value === 'number' && Number.isFinite(value) && value >= 0 ? value : null;
  };

  const { count: lastYearEnrollmentCount, year: lastYear } = useLastYearEnrollment(course.id, course.academicYear);
  const { tags } = useReviewTags();
  const { isCourseAdded, isLoading: scheduleLoading } = useSchedule();

  const selectedYearNumber = selectedYear ? Number(selectedYear) : null;
  const selectedEnrollmentYearString = selectedYear ?? String(lastYear);
  const selectedEnrollmentCount: number | null =
    selectedYearNumber === null
      ? lastYearEnrollmentCount
      : courseYear !== null && selectedYearNumber === courseYear
      ? getStaticEnrollmentCount(courseYear) ?? currentEnrollmentCount
      : courseYear !== null && selectedYearNumber === courseYear - 1
      ? getStaticEnrollmentCount(courseYear - 1) ?? lastYearEnrollmentCount
      : null;
  const displaySelectedYearEnrollment = selectedEnrollmentCount === null ? '--' : `${selectedEnrollmentCount}人`;
  const dayPeriodLabel = getDayPeriodLabel(course);
  const surveyCardGap = 12;
  const surveyCardWidth = Math.min(520, Math.max(240, windowWidth - 96));

  useEffect(() => {
    setActiveSurveyIndex(0);
  }, [selectedYearForHook, surveyResponses.length]);

  const isEnrolled = isCourseAdded(course.id, course.academicYear);
  const checkingEnrollment = scheduleLoading;
  const hasCurrentYearData = dataAcademicYear === selectedYearForHook;
  const canShowReviewerAttributes = !loading && hasCurrentYearData && reviewCount > MIN_PUBLIC_REVIEW_COUNT;

  const tagNameMap = useMemo(() => {
    const map: Record<string, string> = {};
    tags.forEach((tag) => {
      map[tag.id] = tag.name;
    });
    return map;
  }, [tags]);

  const tagCategoryMap = useMemo(() => {
    const map: Record<string, string> = {};
    tags.forEach((tag) => {
      map[tag.id] = tag.category;
    });
    return map;
  }, [tags]);

  const atmosphereTopTags = useMemo(() => {
    if (!stats) return [] as [string, number][];
    return Object.entries(stats.tagCounts)
      .filter(([tagId]) => tagCategoryMap[tagId] === 'class_atmosphere')
      .sort((a, b) => b[1] - a[1])
      .slice(0, 8);
  }, [stats, tagCategoryMap]);

  const contentTopTags = useMemo(() => {
    if (!stats) return [] as [string, number][];
    return Object.entries(stats.tagCounts)
      .filter(([tagId]) => tagCategoryMap[tagId] !== 'class_atmosphere')
      .sort((a, b) => b[1] - a[1])
      .slice(0, 8);
  }, [stats, tagCategoryMap]);

  const reviewerYearData = useMemo(() => {
    return YEAR_SEGMENT_CONFIG.map((item) => ({
      label: item.label,
      value: Number(stats?.reviewerYearCounts?.[item.key] || 0),
      color: item.color,
    }));
  }, [stats]);

  const reviewerFacultyData = useMemo(() => {
    return FACULTY_SEGMENT_CONFIG.map((item) => ({
      label: item.label,
      value: Number(stats?.reviewerFacultyCounts?.[item.key] || 0),
      color: item.color,
    }));
  }, [stats]);

  const reviewerAttributeCount = useMemo(() => {
    return reviewerYearData.reduce((sum, item) => sum + item.value, 0);
  }, [reviewerYearData]);

  const gradeInputCount = Object.values(gradeDistribution || {}).reduce((sum, count) => sum + count, 0);

  return (
    <View style={styles.screenContainer}>
      <ScrollView style={[styles.container, { backgroundColor: theme.colors.background }]} contentContainerStyle={styles.content}>
        <Card style={styles.card}>
          <Card.Content>
            <Text style={[styles.title, { color: theme.colors.onSurface }]}>{course.name}</Text>
            <Text style={[styles.subtitle, { color: theme.colors.onSurfaceVariant }]}>
              {course.professor || t("教員情報なし")}
            </Text>
            <View style={styles.metaRow}>
              {course.academicYear ? (
                <Chip compact style={{ backgroundColor: theme.colors.surfaceVariant }} textStyle={{ color: theme.colors.onSurfaceVariant }}>
                  {course.academicYear}
                </Chip>
              ) : null}
              {course.semester ? (
                <Chip compact style={{ backgroundColor: theme.colors.surfaceVariant }} textStyle={{ color: theme.colors.onSurfaceVariant }}>
                  {t(course.semester)}
                </Chip>
              ) : null}
              {dayPeriodLabel ? (
                <Chip compact style={{ backgroundColor: theme.colors.surfaceVariant }} textStyle={{ color: theme.colors.onSurfaceVariant }}>
                  {t(dayPeriodLabel)}
                </Chip>
              ) : null}
            </View>
            {availableYears.length > 0 && (
              <View style={styles.yearToggleRow}>
                <View style={[styles.yearToggleTrack, { backgroundColor: theme.colors.surfaceVariant }]}>
                  {availableYears.map((year) => {
                    const selected = selectedYear === year;
                    return (
                      <TouchableOpacity
                        key={year}
                        onPress={() => setSelectedYear(year)}
                        activeOpacity={0.8}
                        style={[
                          styles.yearToggleButton,
                          selected && {
                            backgroundColor: theme.colors.primary,
                          },
                        ]}
                      >
                        <Text
                          style={[
                            styles.yearToggleButtonText,
                            {
                              color: selected ? theme.colors.onPrimary : theme.colors.onSurfaceVariant,
                            },
                          ]}
                        >
                          {t('{0}年度', [year])}</Text>
                      </TouchableOpacity>
                    );
                  })}
                </View>
              </View>
            )}
            <Text style={[styles.metaText, { color: theme.colors.onSurfaceVariant }]}>{t("現在履修中:")} {currentEnrollmentCount === null ? '--' : t("{0}人", [currentEnrollmentCount])}</Text>
            <Text style={[styles.metaText, { color: theme.colors.onSurfaceVariant }]}>
              {t('{0}年度履修者: {1}', [selectedEnrollmentYearString, displaySelectedYearEnrollment])}
            </Text>
            <Divider style={{ marginVertical: 12 }} />
            <View style={styles.statsRow}>
              <View style={styles.statItem}>
                <Text style={[styles.statNumber, { color: theme.colors.onSurface }]}>{reviewCount}</Text>
                <Text style={[styles.statLabel, { color: theme.colors.onSurfaceVariant }]}>{t("レビュー件数")}</Text>
                {selectedEnrollmentCount !== null && selectedEnrollmentCount > 0 ? (
                  <>
                    <View style={[styles.reliabilityTrack, { backgroundColor: theme.colors.surfaceVariant }]}>
                      <View
                        style={[
                          styles.reliabilityFill,
                          {
                            width: `${Math.min(Math.round((reviewCount / selectedEnrollmentCount) * 100), 100)}%`,
                            backgroundColor: theme.colors.primary,
                          },
                        ]}
                      />
                    </View>
                    <Text style={[styles.reliabilityLabel, { color: theme.colors.onSurfaceVariant }]}>
                      {t('{0}年度{1}人中 {2}%', [selectedEnrollmentYearString, selectedEnrollmentCount, Math.round((reviewCount / selectedEnrollmentCount) * 100)])}
                    </Text>
                  </>
                ) : null}
              </View>
              <View style={[styles.statDivider, { backgroundColor: theme.colors.outlineVariant }]} />
              <View style={styles.statItem}>
                <Text style={[styles.statNumber, { color: theme.colors.onSurface }]}>{gradeInputCount}</Text>
                <Text style={[styles.statLabel, { color: theme.colors.onSurfaceVariant }]}>{t("成績入力件数")}</Text>
                {selectedEnrollmentCount !== null && selectedEnrollmentCount > 0 ? (
                  <>
                    <View style={[styles.reliabilityTrack, { backgroundColor: theme.colors.surfaceVariant }]}>
                      <View
                        style={[
                          styles.reliabilityFill,
                          {
                            width: `${Math.min(Math.round((gradeInputCount / selectedEnrollmentCount) * 100), 100)}%`,
                            backgroundColor: theme.colors.primary,
                          },
                        ]}
                      />
                    </View>
                    <Text style={[styles.reliabilityLabel, { color: theme.colors.onSurfaceVariant }]}>
                      {t('{0}年度{1}人中 {2}%', [selectedEnrollmentYearString, selectedEnrollmentCount, Math.round((gradeInputCount / selectedEnrollmentCount) * 100)])}
                    </Text>
                  </>
                ) : null}
              </View>
            </View>
          </Card.Content>
        </Card>

        {canShowReviewerAttributes && (
          <Card style={styles.card}>
            <Card.Content>
              {reviewerAttributeCount > 0 ? (
                <>
                  <StackedBar data={reviewerYearData} title={t("学年分布")} theme={theme} />
                  <StackedBar data={reviewerFacultyData} title={t("学部分布")} theme={theme} />
                </>
              ) : (
                <Text style={[styles.attributeNote, { color: theme.colors.onSurfaceVariant }]}>{t("属性データがありません")}</Text>
              )}
            </Card.Content>
          </Card>
        )}

        <Card style={styles.card}>
          <Card.Content>
            <Text style={[styles.sectionTitle, { color: theme.colors.onSurface }]}>{t("授業レビュー統計")}</Text>
            {loading || !hasCurrentYearData ? (
              <ActivityIndicator />
            ) : error ? (
              <Text style={[styles.metaText, { color: theme.colors.error }]}>{error}</Text>
            ) : !stats ? (
              <Text style={[styles.emptyText, { color: theme.colors.onSurfaceVariant }]}>{t("表示可能なレビュー統計がありません")}</Text>
            ) : (
              <>
                <View style={{ marginTop: 8 }}>
                  {renderStatSlider(t("課題の重さ"), t("軽い"), t("重い"), stats.workloadAverage, '#5C7FA5', theme)}
                  {renderStatSlider(t("授業の形式"), t("講義"), t("発表/議論"), stats.formatAverage, '#8E6C3B', theme)}
                  {renderStatSlider(t("期末試験/レポート難易度"), t("易しい"), t("難しい"), stats.examDifficultyAverage, '#5C7FA5', theme)}
                </View>
                <Divider style={[styles.divider, { backgroundColor: theme.colors.outlineVariant }]} />
                <Text style={[styles.sectionTitle, { color: theme.colors.onSurface }]}>{t("期末試験形態")}</Text>
                <View style={styles.tagWrap}>
                  {stats.examTypeCounts.in_person > 0 && (
                    <Chip compact style={[styles.tagChip, { backgroundColor: theme.colors.secondaryContainer }]} textStyle={{ color: theme.colors.onSecondaryContainer }}>
                      {t("対面試験 (")}{stats.examTypeCounts.in_person})
                    </Chip>
                  )}
                  {stats.examTypeCounts.online > 0 && (
                    <Chip compact style={[styles.tagChip, { backgroundColor: theme.colors.secondaryContainer }]} textStyle={{ color: theme.colors.onSecondaryContainer }}>
                      {t("オンライン試験 (")}{stats.examTypeCounts.online})
                    </Chip>
                  )}
                  {stats.examTypeCounts.none > 0 && (
                    <Chip compact style={[styles.tagChip, { backgroundColor: theme.colors.secondaryContainer }]} textStyle={{ color: theme.colors.onSecondaryContainer }}>
                      {t("試験なし (")}{stats.examTypeCounts.none})
                    </Chip>
                  )}
                  {stats.examTypeCounts.in_person === 0 && stats.examTypeCounts.online === 0 && stats.examTypeCounts.none === 0 && (
                    <Text style={[styles.emptyText, { color: theme.colors.onSurfaceVariant }]}>{t("データがありません")}</Text>
                  )}
                </View>

                <Divider style={[styles.divider, { backgroundColor: theme.colors.outlineVariant }]} />
                <Text style={[styles.sectionTitle, { color: theme.colors.onSurface }]}>{t("雰囲気タグ")}</Text>
                <View style={styles.tagWrap}>
                  {atmosphereTopTags.map(([tagId, count]) => (
                    <Chip key={tagId} compact style={[styles.tagChip, { backgroundColor: theme.colors.surfaceVariant }]} textStyle={{ color: theme.colors.onSurfaceVariant }}>
                      {t(tagNameMap[tagId] || tagId) + ` (${count})`}
                    </Chip>
                  ))}
                  {atmosphereTopTags.length === 0 && (
                    <Text style={[styles.emptyText, { color: theme.colors.onSurfaceVariant }]}>{t("タグデータがありません")}</Text>
                  )}
                </View>

                <Divider style={[styles.divider, { backgroundColor: theme.colors.outlineVariant }]} />
                <Text style={[styles.sectionTitle, { color: theme.colors.onSurface }]}>{t("学習内容タグ")}</Text>
                <View style={styles.tagWrap}>
                  {contentTopTags.map(([tagId, count]) => (
                    <Chip key={tagId} compact style={[styles.tagChip, { backgroundColor: theme.colors.surfaceVariant }]} textStyle={{ color: theme.colors.onSurfaceVariant }}>
                      {t(tagNameMap[tagId] || tagId) + ` (${count})`}
                    </Chip>
                  ))}
                  {contentTopTags.length === 0 && (
                    <Text style={[styles.emptyText, { color: theme.colors.onSurfaceVariant }]}>{t("タグデータがありません")}</Text>
                  )}
                </View>
              </>
            )}
          </Card.Content>
        </Card>

        <Card style={styles.card}>
          <Card.Content>
            <Text style={[styles.sectionTitle, { color: theme.colors.onSurface }]}>{t("成績分布")}</Text>
            {renderBarChart(gradeDistribution as Record<string, number>, theme)}
          </Card.Content>
        </Card>

        <Card style={styles.card}>
          <Card.Content>
            <Text style={[styles.sectionTitle, { color: theme.colors.onSurface }]}>{t("受講者コメント")}</Text>
            {surveyLoading ? <ActivityIndicator /> : surveyError ? (
              <Text style={[styles.emptyText, { color: theme.colors.error }]}>{surveyError}</Text>
            ) : surveyResponses.length === 0 ? (
              <Text style={[styles.emptyText, { color: theme.colors.onSurfaceVariant }]}>{t("受講者コメントはありません")}</Text>
            ) : (
              <>
                <FlatList
                  data={surveyResponses}
                  keyExtractor={(response) => response.id}
                  horizontal
                  showsHorizontalScrollIndicator={false}
                  contentContainerStyle={styles.responseListContent}
                  ItemSeparatorComponent={() => <View style={{ width: surveyCardGap }} />}
                  snapToInterval={surveyCardWidth + surveyCardGap}
                  snapToAlignment="start"
                  decelerationRate="fast"
                  onScroll={(event) => {
                    const nextIndex = Math.max(0, Math.min(
                      surveyResponses.length - 1,
                      Math.round(event.nativeEvent.contentOffset.x / (surveyCardWidth + surveyCardGap)),
                    ));
                    if (nextIndex !== activeSurveyIndex) setActiveSurveyIndex(nextIndex);
                  }}
                  scrollEventThrottle={16}
                  renderItem={({ item: response, index }) => (
                    <View
                      testID={`survey-response-card-${index + 1}`}
                      style={[
                        styles.responseCard,
                        {
                          width: surveyCardWidth,
                          backgroundColor: theme.colors.surface,
                          borderColor: theme.colors.outlineVariant,
                        },
                      ]}
                    >
                      <Text style={[styles.responseLabel, { color: theme.colors.onSurfaceVariant }]}>{t("回答")} {index + 1}</Text>
                      {(['advice', 'positivePoints', 'negativePoints'] as const).map((field, fieldIndex) => {
                        const value = response[field];
                        const hasAnswer = typeof value === 'string' && value.trim().length > 0;
                        const iconName = ({
                          advice: 'warning-outline',
                          positivePoints: 'happy-outline',
                          negativePoints: 'sad-outline',
                        } as const)[field];
                        return (
                          <View key={field}>
                            {fieldIndex > 0 ? <Divider style={[styles.responseDivider, { backgroundColor: theme.colors.outlineVariant }]} /> : null}
                            <View style={styles.responseAnswer}>
                              <View style={styles.responseHeadingRow}>
                                <Ionicons name={iconName} size={20} color={theme.colors.primary} style={styles.responseIcon} />
                                <Text style={[styles.responseHeading, { color: theme.colors.onSurface }]}>{SURVEY_RESPONSE_DISPLAY_HEADINGS[field]}</Text>
                              </View>
                              <Text style={[styles.responseBody, { color: hasAnswer ? theme.colors.onSurface : theme.colors.onSurfaceVariant }]}>
                                {hasAnswer ? value : t("回答なし")}
                              </Text>
                            </View>
                          </View>
                        );
                      })}
                    </View>
                  )}
                />
                {surveyResponses.length > 1 ? (
                  <Text style={[styles.responsePosition, { color: theme.colors.onSurfaceVariant }]}>
                    {activeSurveyIndex + 1} / {surveyResponses.length}
                  </Text>
                ) : null}
              </>
            )}
          </Card.Content>
        </Card>

        <View style={styles.actionRow}>
          <Button
            mode="outlined"
            onPress={() => navigation.navigate('GradeSubmit', { course })}
            disabled={!auth.currentUser || checkingEnrollment || !isEnrolled || !isInGradePeriod()}
          >
            {t("成績を入力")}
          </Button>
          {!auth.currentUser ? <Text style={[styles.helperText, { color: theme.colors.onSurfaceVariant }]}>{t("投稿にはログインが必要です")}</Text> : null}
          {auth.currentUser && !checkingEnrollment && !isEnrolled ? (
            <Text style={[styles.helperText, { color: theme.colors.onSurfaceVariant }]}>{t("この授業の履修者のみ投稿できます")}</Text>
          ) : null}
        </View>
      </ScrollView>
      <FAB
        icon={({ size, color }) => <Ionicons name="create-outline" size={size} color={color} />}
        label={t("レビューを投稿")}
        style={styles.fab}
        onPress={() => navigation.navigate('CourseReviewForm', { course })}
        disabled={!auth.currentUser || checkingEnrollment || !isEnrolled || !isInReviewPeriod()}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  screenContainer: {
    flex: 1,
    position: 'relative',
  },
  container: {
    flex: 1,
  },
  content: {
    padding: 16,
    paddingBottom: 100,
    gap: 12,
  },
  card: {
    elevation: 2,
  },
  title: {
    fontSize: 22,
    fontWeight: '700',
  },
  subtitle: {
    marginTop: 6,
    fontSize: 14,
  },
  metaRow: {
    flexDirection: 'row',
    gap: 8,
    marginTop: 10,
    marginBottom: 10,
  },
  yearToggleRow: {
    marginBottom: 10,
  },
  yearToggleTrack: {
    flexDirection: 'row',
    alignSelf: 'flex-start',
    borderRadius: 999,
    padding: 3,
    gap: 4,
  },
  yearToggleButton: {
    paddingHorizontal: 14,
    paddingVertical: 7,
    borderRadius: 999,
  },
  yearToggleButtonText: {
    fontSize: 13,
    fontWeight: '600',
  },
  metaText: {
    fontSize: 14,
    marginBottom: 4,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '700',
    marginBottom: 10,
  },
  gradeBarContainer: {
    marginTop: 4,
  },
  gradeBarRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 6,
  },
  gradeLabel: {
    fontSize: 12,
    fontWeight: '700',
    width: 28,
    textAlign: 'right',
    marginRight: 8,
  },
  gradeBarTrack: {
    flex: 1,
    height: 18,
    borderRadius: 9,
    overflow: 'hidden',
  },
  gradeBarFill: {
    height: '100%',
    borderRadius: 9,
  },
  gradeCount: {
    fontSize: 12,
    fontWeight: '700',
    width: 24,
    textAlign: 'right',
    marginLeft: 6,
  },
  divider: {
    marginVertical: 14,
  },
  emptyText: {},
  tagWrap: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
  },
  tagChip: {
    marginBottom: 6,
    marginRight: 4,
  },
  actionRow: {
    marginTop: 4,
    marginBottom: 20,
  },
  helperText: {
    marginTop: 8,
    fontSize: 12,
  },
  fab: {
    position: 'absolute',
    right: 16,
    bottom: 24,
  },
  statsRow: {
    flexDirection: 'row',
    alignItems: 'stretch',
    marginTop: 4,
  },
  statItem: {
    flex: 1,
    alignItems: 'center',
  },
  statNumber: {
    fontSize: 20,
    fontWeight: '700',
  },
  statLabel: {
    fontSize: 11,
    marginTop: 2,
  },
  statDivider: {
    width: 1,
    marginHorizontal: 8,
  },
  attributeNote: {
    fontSize: 11,
    marginBottom: 12,
    fontStyle: 'italic',
  },
  responseCard: {
    borderWidth: 1,
    borderRadius: 12,
    padding: 16,
  },
  responseListContent: {
    paddingRight: 16,
  },
  responseLabel: {
    fontSize: 12,
    marginBottom: 16,
  },
  responseHeadingRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 8,
  },
  responseIcon: {
    width: 20,
    flexShrink: 0,
  },
  responseAnswer: {
    gap: 8,
  },
  responseHeading: {
    fontSize: 14,
    fontWeight: '700',
    flex: 1,
  },
  responseBody: {
    fontSize: 14,
    lineHeight: 21,
  },
  responseDivider: {
    marginVertical: 16,
  },
  responsePosition: {
    fontSize: 12,
    marginTop: 12,
    textAlign: 'center',
  },
  reliabilityTrack: {
    alignSelf: 'stretch',
    height: 4,
    borderRadius: 2,
    marginTop: 6,
    marginHorizontal: 8,
    overflow: 'hidden',
  },
  reliabilityFill: {
    height: '100%',
    borderRadius: 2,
  },
  reliabilityLabel: {
    fontSize: 10,
    marginTop: 3,
    textAlign: 'center',
  },
});

export default CourseEvaluationScreen;
